/* The review queue and the approve mutation.
 *
 * No `X-Lumiq-Tenant` header, here or anywhere. Hard rule 1: the edge sets it from a validated token
 * claim, and a frontend that sends it is asking the backend to trust the browser — which is the
 * whole vulnerability. `eslint.config.js` fails the build on a literal matching that header name.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';

import { isClassification } from '@/shared/api/generated/catalog';
import type { Classification } from '@/shared/api/generated/catalog';
import { getPage, post } from '@/shared/api/http';

import { orderForReview, toReviewRow } from '../model/review';
import type { ReviewRow } from '../model/review';

export const REVIEW_QUEUE_URL = '/v1/catalog/classifications/review-queue';

export function approveUrl(datasetId: string): string {
  return `/v1/catalog/datasets/${encodeURIComponent(datasetId)}/classification/approve`;
}

const QUERY_KEY = ['catalog', 'classifications', 'review-queue'] as const;

/* RFC 9457 Problem Details. Every backend error serialises to this, so the UI can show the server's
 * own explanation rather than inventing one — "cannot approve a verdict computed against a different
 * schema state" is far more useful to a reviewer than "Request failed". */

async function fetchReviewQueue(): Promise<readonly ReviewRow[]> {
  /* `getPage` validates per item with the generated guard, not a cast. A cast would let a malformed
   * record through with `state: undefined`, and the tone lookup would render it as an empty cell — a
   * PII classification silently displayed as nothing at all. Refusing the whole response is the loud
   * failure; showing eleven of twelve rows would be the quiet one. */
  const page = await getPage(REVIEW_QUEUE_URL, isClassification, 'review queue item');
  return orderForReview(page.items.map(toReviewRow));
}

/** `enabled: false` means "do not ask", and it is a permission answer rather than a lazy-load.
 *
 * A caller that knows it lacks `read:catalog.classification` must not issue the request: a 403 arrives
 * as a failed read, and a surface aggregating several sources then has to report an ordinary
 * permission boundary as "we could not find out". Optional and defaulted, so the review screen and the
 * dashboard are untouched — `useIncidentQueue` carries the same option for the same reason. */
export interface ReviewQueryOptions {
  readonly enabled?: boolean;
}

export function useReviewQueue(
  options: ReviewQueryOptions = {},
): UseQueryResult<readonly ReviewRow[]> {
  return useQuery({
    queryKey: QUERY_KEY,
    queryFn: fetchReviewQueue,
    enabled: options.enabled ?? true,
    retry: false,
  });
}

/* One key per dataset, generated when the row is rendered rather than when the button is clicked.
 *
 * That distinction is the whole point of the header. `crypto.randomUUID()` inside the click handler
 * would mint a fresh key per attempt, so a user double-clicking through a slow network would send two
 * requests the server treats as unrelated. Keying per dataset means a retry of the same decision
 * carries the same key.
 *
 * `crypto.randomUUID` needs a secure context; served over HTTPS or from localhost, which covers
 * every deployment and dev. */
const keys = new Map<string, string>();

export function idempotencyKeyFor(datasetId: string): string {
  const existing = keys.get(datasetId);
  if (existing !== undefined) return existing;
  const minted = `approve-${datasetId}-${crypto.randomUUID()}`;
  keys.set(datasetId, minted);
  return minted;
}

async function approve(datasetId: string): Promise<Classification> {
  return post(
    approveUrl(datasetId),
    idempotencyKeyFor(datasetId),
    isClassification,
    'classification',
  );
}

export function useApproveClassification(): UseMutationResult<
  Classification,
  Error,
  string
> {
  const client = useQueryClient();
  return useMutation({
    mutationFn: approve,
    /* Refetch rather than patch the cache optimistically. An approval can be refused server-side —
     * the schema may have moved since the row was rendered, which is a 409 — so the authoritative
     * state after the call is whatever the server now says, not what the click assumed. */
    onSettled: () => client.invalidateQueries({ queryKey: QUERY_KEY }),
    retry: false,
  });
}
